import { Button, Collapse, Form, Select, Spin, notification } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import LoadingBox from "../../Components/LoadingBox";
import { API } from "../../Config";
import { GET, POST } from "../../Config/ApiRequests";
import PageHeader from "../../Routes/PageHeader";
import "./styles.css";

const { Panel } = Collapse;

const UserAccess = () => {
  const [loading1, setLoading1] = useState(true);
  const [loading2, setLoading2] = useState(false);
  const [loading3, setLoading3] = useState(false);
  const [form] = Form.useForm();
  const [allUsers, setAllUsers] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedIds, setSelectedIds] = useState("");
  const [selectedSubModuleIds, setSelectedSubModuleIds] = useState("");
  const [selectedLedgerDescriptions, setSelectedLedgerDescriptions] =
    useState("");

  const [moduleToNumberMapping, setModuleToNumberMapping] = useState({});
  const [Notifications, contextHolder] = notification.useNotification();

  // Utility function to extract numeric part from description
  const extractNumber = (description) => {
    if (!description) return "";
    const parts = description.split("-");
    return parts[parts.length - 1] || "";
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      await Promise.all([getAllModules(), getAllUsers()]);
    };
    fetchInitialData();
  }, []);

  const getIdsSet = (idsString) =>
    new Set(idsString.split("|").filter(Boolean));

  const mainModules = useMemo(
    () => modules.filter((module) => Number(module.module_id) < 65),
    [modules]
  );

  const subLedgerModules = useMemo(
    () => modules.filter((module) => Number(module.module_id) >= 65),
    [modules]
  );

  const allSelected = useMemo(() => {
    const idsSet = getIdsSet(selectedIds);
    const subModuleIdsSet = getIdsSet(selectedSubModuleIds);
    return (
      mainModules.every((module) => idsSet.has(module.module_id)) &&
      subLedgerModules.every((module) => subModuleIdsSet.has(module.module_id))
    );
  }, [selectedIds, selectedSubModuleIds, mainModules, subLedgerModules]);

  const allSubLedgersSelected = useMemo(() => {
    const subModuleIdsSet = getIdsSet(selectedSubModuleIds);
    return subLedgerModules.every((module) =>
      subModuleIdsSet.has(module.module_id)
    );
  }, [selectedSubModuleIds, subLedgerModules]);

  const getAllUsers = async () => {
    try {
      setLoading1(true);
      const users = await GET(API.ALL_USERS, null);
      setAllUsers(users?.status ? users.data : []);
    } catch (err) {
      console.error("Error fetching users:", err);
      setAllUsers([]);
      Notifications["error"]({ message: "Failed to fetch users" });
    } finally {
      setLoading1(false);
    }
  };

  const getAllModules = async () => {
    try {
      setLoading1(true);
      const modulesData = await POST(API.LIST_MODULES, {});
      if (modulesData?.status) {
        setModules(modulesData.data);
        const descMapping = {};
        const numMapping = {};
        modulesData.data.forEach((module) => {
          if (module.description) {
            descMapping[module.module_id] = module.description;
            numMapping[module.module_id] = extractNumber(module.description);
          }
        });
        setModuleToNumberMapping(numMapping);
      } else {
        setModules([]);
      }
    } catch (err) {
      console.error("Error fetching modules:", err);
      setModules([]);
      Notifications["error"]({ message: "Failed to fetch modules" });
    } finally {
      setLoading1(false);
    }
  };

  const getUserDetails = async () => {
    if (!selectedUser) return;
    try {
      setLoading3(true);
      const details = await GET(`${API.ONE_USER}${selectedUser}`, null);
      if (details?.status) {
        setUserDetails(details.data);
        const existingAccess = details.data?.user_access || "";
        const existingLedgerAccess = details.data?.ledger_access || "";

        const accessIds = getIdsSet(existingAccess);
        const mainIds = [];
        const subIds = [];

        accessIds.forEach((id) => {
          const numId = Number(id);
          if (numId < 65) {
            mainIds.push(id);
          } else {
            subIds.push(id);
          }
        });

        setSelectedIds(mainIds.join("|"));
        setSelectedSubModuleIds(subIds.join("|"));

        // Filter ledger_access numbers to match selected sub-module IDs
        const ledgerNumbers = getIdsSet(existingLedgerAccess);
        const validNumbers = subLedgerModules
          .filter((module) => subIds.includes(module.module_id))
          .map((module) => moduleToNumberMapping[module.module_id])
          .filter(Boolean)
          .join("|");
        setSelectedLedgerDescriptions(validNumbers);
      } else {
        setUserDetails({});
        setSelectedIds("");
        setSelectedSubModuleIds("");
        setSelectedLedgerDescriptions("");
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setUserDetails({});
      Notifications["error"]({ message: "Failed to fetch user details" });
    } finally {
      setLoading3(false);
    }
  };

  const toggleCheckbox = (id, isLedger = false) => {
    if (isLedger) {
      const subModuleIdsSet = getIdsSet(selectedSubModuleIds);
      const ledgerNumbersSet = getIdsSet(selectedLedgerDescriptions);

      if (subModuleIdsSet.has(id)) {
        subModuleIdsSet.delete(id);
        const number = moduleToNumberMapping[id];
        if (number) {
          ledgerNumbersSet.delete(number);
        }
      } else {
        subModuleIdsSet.add(id);
        const number = moduleToNumberMapping[id];
        if (number) {
          ledgerNumbersSet.add(number);
        }
      }

      setSelectedSubModuleIds(Array.from(subModuleIdsSet).join("|"));
      setSelectedLedgerDescriptions(Array.from(ledgerNumbersSet).join("|"));

      const idsSet = getIdsSet(selectedIds);
      if (!idsSet.has("64")) {
        idsSet.add("64");
        setSelectedIds(Array.from(idsSet).join("|"));
      }
    } else {
      const idsSet = getIdsSet(selectedIds);
      if (idsSet.has(id)) {
        idsSet.delete(id);
        if (id === "64") {
          setSelectedSubModuleIds("");
          setSelectedLedgerDescriptions("");
        }
      } else {
        idsSet.add(id);
        if (id === "64") {
          const subModuleIdsSet = getIdsSet(selectedSubModuleIds);
          const ledgerNumbersSet = getIdsSet(selectedLedgerDescriptions);

          subLedgerModules.forEach((module) => {
            subModuleIdsSet.add(module.module_id);
            const number = moduleToNumberMapping[module.module_id];
            if (number) {
              ledgerNumbersSet.add(number);
            }
          });

          setSelectedSubModuleIds(Array.from(subModuleIdsSet).join("|"));
          setSelectedLedgerDescriptions(Array.from(ledgerNumbersSet).join("|"));
        }
      }
      setSelectedIds(Array.from(idsSet).join("|"));
    }
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds("");
      setSelectedSubModuleIds("");
      setSelectedLedgerDescriptions("");
    } else {
      const allMainIds = mainModules
        .map((module) => module.module_id)
        .join("|");
      const allSubModuleIds = subLedgerModules
        .map((module) => module.module_id)
        .join("|");
      const allLedgerNumbers = subLedgerModules
        .map((module) => moduleToNumberMapping[module.module_id])
        .filter(Boolean)
        .join("|");

      setSelectedIds(allMainIds);
      setSelectedSubModuleIds(allSubModuleIds);
      setSelectedLedgerDescriptions(allLedgerNumbers);
    }
  };

  const toggleSelectAllSubLedgers = () => {
    const subModuleIdsSet = getIdsSet(selectedSubModuleIds);
    const ledgerNumbersSet = getIdsSet(selectedLedgerDescriptions);

    if (allSubLedgersSelected) {
      subLedgerModules.forEach((module) => {
        subModuleIdsSet.delete(module.module_id);
        const number = moduleToNumberMapping[module.module_id];
        if (number) {
          ledgerNumbersSet.delete(number);
        }
      });
    } else {
      subLedgerModules.forEach((module) => {
        subModuleIdsSet.add(module.module_id);
        const number = moduleToNumberMapping[module.module_id];
        if (number) {
          ledgerNumbersSet.add(number);
        }
      });
    }

    setSelectedSubModuleIds(Array.from(subModuleIdsSet).join("|"));
    setSelectedLedgerDescriptions(Array.from(ledgerNumbersSet).join("|"));

    const idsSet = getIdsSet(selectedIds);
    if (!idsSet.has("64")) {
      idsSet.add("64");
      setSelectedIds(Array.from(idsSet).join("|"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading3(true);
      const allAccessIds = [
        ...getIdsSet(selectedIds),
        ...getIdsSet(selectedSubModuleIds),
      ];
      const combinedAccess = Array.from(new Set(allAccessIds)).join("|");

      const update = await POST(API.UPDATE_ACCESS, {
        user_id: Number(selectedUser),
        access: combinedAccess,
        ledger_access: selectedLedgerDescriptions,
      });

      if (update?.status) {
        Notifications["success"]({ message: "Updated" });
        await Promise.all([getAllUsers(), getAllModules()]);
      } else {
        Notifications["error"]({ message: "Unable to update" });
      }
    } catch (error) {
      console.error("Error updating access:", error);
      Notifications["error"]({ message: "Server error" });
    } finally {
      setLoading3(false);
    }
  };

  return (
    <div>
      {contextHolder}
      <PageHeader PageName="User Access" breadScrum="Admin / UserAccess" />
      {loading1 ? (
        <LoadingBox />
      ) : (
        <>
          <div className="card1">
            <Form form={form} layout="vertical">
              <div>
                <div className="card-header">Select Staff</div>
                <br />
                <Row>
                  <Col md={4}>
                    <Form.Item
                      name="from_date"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Select
                        size="large"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        placeholder="Select User"
                        onChange={setSelectedUser}
                        onSearch={true}
                      >
                        {/* {allUsers.map((user) => (
                          <Select.Option key={user.id} value={user.id}>
                          {item?.account_no?.[0] ? {user.name} - {item?.account_no?.[0]}  {user.name}}
                          </Select.Option>
                        ))} */}
                        {allUsers.map((user) => (
                          <Select.Option key={user.id} value={user.id}>
                            {user?.account_no?.[0]
                              ? `${user.name} - ${user.account_no[0]}`
                              : user.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col md={1} />
                  <Col md={2}>
                    <Button
                      size="large"
                      style={{ width: "100%" }}
                      loading={loading2}
                      onClick={getUserDetails}
                    >
                      Search
                    </Button>
                  </Col>
                </Row>
              </div>
            </Form>
          </div>
          <br />
          {userDetails?.id && (
            <div className="card1">
              <Container>
                <Row>
                  <Col md={4}>
                    <div className="text-item">
                      <div className="text-label">Name</div>
                      <div className="text-value">{userDetails.name}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-item">
                      <div className="text-label">Email</div>
                      <div className="text-value">{userDetails.email}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-item">
                      <div className="text-label">Branch</div>
                      <div className="text-value">
                        {userDetails.branch?.name}
                      </div>
                    </div>
                  </Col>
                </Row>
                <hr />
                <br />
                <form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={10}>
                      <div className="txt1">Select Screens</div>
                    </Col>
                    <Col md={2}>
                      <div
                        onClick={toggleSelectAll}
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                        />
                        Select All Screens
                      </div>
                    </Col>
                  </Row>
                  <br />
                  <table style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th></th>
                        <th></th>
                        <th>Module Name</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mainModules.map((module) => (
                        <tr
                          key={module.module_id}
                          onClick={() => toggleCheckbox(module.module_id)}
                        >
                          <td
                            style={{
                              backgroundColor: "#fff",
                              width: "2%",
                              textAlign: "left",
                            }}
                            className="table-td"
                          >
                            {module.module_id}
                          </td>
                          <td
                            style={{ textAlign: "right", width: "3%" }}
                            className="table-td"
                          >
                            <input
                              type="checkbox"
                              checked={getIdsSet(selectedIds).has(
                                module.module_id
                              )}
                              onChange={() => toggleCheckbox(module.module_id)}
                            />
                          </td>
                          <td className="MC-table-Txt table-td">
                            {module.modules_name}
                          </td>
                          <td className="MC-table-Txt table-td">
                            {module.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {subLedgerModules.length > 0 && (
                    <>
                      <br />
                      <Collapse defaultActiveKey={["1"]}>
                        <Panel header="Sub-Ledger Accounts" key="1">
                          <Row>
                            <Col md={8}>
                              <div className="txt1">
                                Select Sub-Ledger Accounts
                              </div>
                            </Col>
                            <Col md={4}>
                              <div
                                onClick={toggleSelectAllSubLedgers}
                                style={{ cursor: "pointer" }}
                              >
                                <input
                                  type="checkbox"
                                  checked={allSubLedgersSelected}
                                  onChange={toggleSelectAllSubLedgers}
                                />
                                  Select All Sub-Ledgers
                              </div>
                            </Col>
                          </Row>
                          <br />
                          <table style={{ width: "100%" }}>
                            <thead>
                              <tr>
                                <th></th>
                                <th></th>
                                <th>Module Name</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subLedgerModules.map((module) => (
                                <tr
                                  key={module.module_id}
                                  onClick={() =>
                                    toggleCheckbox(module.module_id, true)
                                  }
                                >
                                  <td
                                    style={{
                                      backgroundColor: "#fff",
                                      width: "2%",
                                      textAlign: "left",
                                    }}
                                    className="table-td"
                                  >
                                    {module.module_id}
                                  </td>
                                  <td
                                    style={{ textAlign: "right", width: "3%" }}
                                    className="table-td"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={getIdsSet(
                                        selectedSubModuleIds
                                      ).has(module.module_id)}
                                      onChange={() =>
                                        toggleCheckbox(module.module_id, true)
                                      }
                                    />
                                  </td>
                                  <td className="MC-table-Txt table-td">
                                    {module.modules_name}
                                  </td>
                                  <td className="MC-table-Txt table-td">
                                    {module.description}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Panel>
                      </Collapse>
                    </>
                  )}
                  <br />
                  <Row>
                    <Col md={10}></Col>
                    <Col md={2}>
                      <button className="submit-btn" type="submit">
                        {loading3 ? <Spin /> : "Save"}
                      </button>
                    </Col>
                  </Row>
                  <br />
                </form>
              </Container>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserAccess;
